import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import {
  NavLink,
  useActionData,
  useNavigation,
  useSubmit,
} from '@remix-run/react';
import { useEffect, useState } from 'react';

import { LoadingSpinner } from '~/components';
import { Button } from '~/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '~/components/ui/form';
import { Input } from '~/components/ui/input';

import type { UseFormReturn } from 'react-hook-form';
import type { ValidationErrorResponseData } from 'remix-validated-form';

type Props = {
  form: UseFormReturn<{ email: string; password: string }>;
};

export const PasswordForm = ({ form }: Props) => {
  const data = useActionData<ValidationErrorResponseData>();

  const submit = useSubmit();
  const navigation = useNavigation();
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (data?.fieldErrors) {
      form.setError('password', {
        type: 'manual',
        message: data.fieldErrors.password,
      });
      form.setError('email', {
        type: 'manual',
        message: data.fieldErrors.email,
      });
    }
  }, [data, form]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => {
          submit(data, { method: 'post' });
        })}
        method="post"
        className="flex w-full flex-col gap-4"
      >
        <p className="text-neutral-350 mb-2 text-center text-xl font-medium tracking-tight">
          Enter your password
        </p>
        <input type="hidden" name="email" value={form.getValues().email} />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <div className="reltive group">
                  <div className="relative">
                    <Input
                      {...field}
                      autoFocus
                      autoComplete="new-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password..."
                      className="bg-primary-500 border-primary-450 hover:bg-primary-550 hover:border-primary-400 focus:bg-primary-550 focus:shadow-solid active:border-neutral-250 focus:border-primary-450 shadow-primary-550 h-14 text-neutral-300 placeholder:text-neutral-400 hover:shadow active:shadow-none"
                      onKeyDown={(e) =>
                        e.key === 'Enter' && submit(e.currentTarget.form)
                      }
                    />

                    <button
                      type="button"
                      className="absolute top-1/2 right-3 -translate-y-1/2"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-neutral-300" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-neutral-300" />
                      )}
                    </button>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          className="bg-primary-500 border-primary-450 h-14 w-full text-neutral-300"
        >
          {navigation.state !== 'idle' ? (
            <LoadingSpinner className="text-neutral-300" />
          ) : (
            'Log in'
          )}
        </Button>
        <p className="text-neutral-450 text-xxs mt-2 text-center">
          Forgot your password?{' '}
          <NavLink to="/reset">
            <span className="text-neutral-350 decoration-dotted hover:underline">
              Reset password
            </span>
          </NavLink>
        </p>
      </form>
    </Form>
  );
};
